/* eslint-disable max-len */
import {
  SimpleForm,
  required,
  ReferenceInput,
  AutocompleteInput,
  useTranslate,
  TextInput,
  useCreate
} from "react-admin";
import { useNavigate } from "react-router-dom";
import * as User from "../providers/user";
import * as Cognito from "providers/cognito";
import * as Axios from "providers/axios";
import { useCallback, useEffect, useState } from "react";
import { FIND_FIRST_USER_BY_PHONE, GET_ARTISTS, GET_LOCATIONS } from "../gql";
import { useLazyQuery, useQuery } from "@apollo/client";
import { CustomButton } from "./CustomButton";
import CheckUserByPhoneNumber from "./CheckUserByPhoneNumber";
import {
  Avatar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BookFromArtistCalendarFormHelper from "./BookFromArtistCalendarFormHelper";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "yup-phone";
import { FailedOrderIcon } from "assets/Icons";
import { getRawPhoneNumber } from "utils/getRawPhoneNumber";

interface EasyCheckoutProps {
  open: boolean;
  onClose: () => void;
}

interface LocationResult {
  id: string;
  company_id: string;
  timezone: string;
}

export default function EasyCheckout({ open, onClose }: EasyCheckoutProps) {
  const { permissions, user } = User.useUser();
  const { user: cognitoUser } = Cognito.useCognito();
  const { api } = Axios.useAxios();
  const translate = useTranslate();
  const navigate = useNavigate();
  const [create] = useCreate();
  const [errors, setErrors] = useState<any>({});
  const [resetUserInputs, setResetUserInputs] = useState<boolean>(false);
  const [amount, setAmount] = useState<number | string>(0);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isCustomerSearchTriggered, setIsCustomerSearchTriggered] =
    useState(false);
  const [userSelectMode, setUserSelectMode] = useState<string | null>(
    "walk-in"
  );
  const [isCloseIconShaking, setIsCloseIconShaking] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [webViewMessageData, setWebViewMessageData] = useState<any>(null);
  const isCustomErrors = Object.keys(errors)?.length > 0;
  const [findUserByPhone, { data: findUserData }] = useLazyQuery(
    FIND_FIRST_USER_BY_PHONE
  );

  const artistsResult = useQuery(GET_ARTISTS, {
    variables: {
      where: {
        user: {
          is: {
            joined_company_id: { equals: permissions?.companyId }
          }
        },
        is_active: {
          equals: true
        }
      }
    },
    fetchPolicy: "no-cache"
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const data = artistsResult?.data ?? artistsResult?.previousData;

  const locationsResult = useQuery(GET_LOCATIONS, {
    variables: {
      where: {
        company_id: { equals: permissions?.companyId }
      }
    },
    fetchPolicy: "no-cache"
  });
  const locationsData: LocationResult[] =
    (locationsResult?.data ?? locationsResult?.previousData)?.locations ?? [];

  const createValidationSchema = () => {
    const baseSchema = {
      location: yup.object({
        id: yup.string().nullable().required("Location selection required.")
      })
      // artist: yup.object({
      //   id: yup.string().nullable().required("Artist selection required.")
      // })
    };

    const userSchema = {
      walk_in: {},
      search: {
        // phone_number_search: yup
        //   .string()
        //   .test("phone", "Invalid phone number", (value) => {
        //     return isPhoneValid(value);
        //   })
      },
      customers: {
        user_id: yup
          .string()
          .nullable()
          .required("Customer selection required.")
      }
    };
    return yup
      .object()
      .shape({
        ...baseSchema,
        ...userSchema[userSelectMode as keyof typeof userSchema]
      })
      .required();
  };

  const submit = async (submitData: any) => {
    setIsSaving(true);
    let customErrors: any = {};
    if (userSelectMode === "search" && !findUserData?.findFirstUser?.id) {
      customErrors.foundUser = translate(
        "appointment.no_existing_customer_found"
      );
    }
    if (!amount) {
      customErrors.amount = translate("order.amount_required");
    }
    if (Object.keys(customErrors)?.length > 0) {
      setErrors(customErrors);
      setIsSaving(false);
      return;
    }
    try {
      setIsSaving(true);
      const { description, location, user_id, artist } = submitData;

      if (amount === 0) {
        setIsSaving(false);
        throw new Error("Amount cannot be 0");
      }

      const orderPayload = {
        location: location?.id,
        full_amount: 0,
        payment_status: "PAY_IN_STORE",
        ...(userSelectMode === "customers" && {
          user: {
            id: user_id
          }
        }),
        ...(userSelectMode === "search" && {
          user: {
            id: findUserData?.findFirstUser?.id
          }
        })
      };

      create(
        "Order",
        {
          data: orderPayload
        },
        {
          onSuccess: (order) => {
            const freeformPayload = {
              service: { id: undefined },
              whatToAdd: "FREEFORM",
              artist: artist.id ? { id: artist.id } : null,
              price: amount,
              description,
              order: { id: order.id }
            };
            create(
              "TxItem",
              { data: freeformPayload },
              {
                onSuccess: () => {
                  navigate(`/dashboard/Walk-in-order/${order.id}/show`);
                  const action = {
                    type: "stripe.terminal.payment",
                    payload: {
                      orderId: order.id,
                      company_id:
                        user.impersonate_company_id || user.company.id,
                      // customAmount: amount,
                      accessToken: cognitoUser?.access_token
                    }
                  };
                  // @ts-ignore
                  window.ReactNativeWebView.postMessage(JSON.stringify(action));
                  onClose();
                },
                onError: () => {
                  // setLoading(false);
                }
              }
            );
          },
          onError: () => {
            // setLoading(false);
          }
        }
      );
      setTimeout(() => {
        setIsSaving(false);
      }, 3000);
    } catch (error) {
      setIsSaving(false);
      setErrors({ networkError: error?.response?.data?.message });
    }
  };

  const disabled =
    permissions.role.role !== "OWNER" &&
    permissions.role.role !== "MANAGER" &&
    !permissions.isSuperAdmin;

  const checkUser = (phoneNumber: string) => {
    findUserByPhone({
      variables: {
        where: {
          OR: [
            {
              phone_number: {
                equals: phoneNumber
              }
            },
            {
              libphonenumber: {
                equals: getRawPhoneNumber(phoneNumber)
              }
            }
          ]
        }
      },
      fetchPolicy: "no-cache"
    });
  };

  const handleMessage = useCallback(
    (event: any) => {
      if (
        event &&
        event?.data &&
        event?.origin !== window.location.origin &&
        event?.data?.action !== "card-element-link-report" &&
        event?.data?.action !== "card-element-link-load"
      ) {
        let messageObject: any = null;

        // In RN WebView, `event.data` is usually a JSON string.
        // In regular web (Stripe/CardElement), `event.data` is often an object.
        if (typeof event?.data === "string") {
          try {
            messageObject = JSON.parse(event.data);
          } catch (err) {}
        } else if (event?.data && typeof event.data === "object") {
          messageObject = event.data;
        }

        if (messageObject) {
          setWebViewMessageData(messageObject);
        }
        if (messageObject.status === "fail") {
          setError(messageObject?.message);
        }
        if (
          messageObject?.status === "success" ||
          messageObject?.status === "timeout"
        ) {
          setError(null);
        }

        if (messageObject?.action === "SEND_PAYMENT_INTENT_MESSAGE") {
          const paymentIntentId = messageObject?.paymentIntentId;
          const orderId = messageObject?.orderId;
          api
            .post(`/v1/payment-intent`, {
              paymentIntentId,
              orderId,
              sub: cognitoUser?.id
            })
            .then((response) => {
              // refresh();
              // handleResetAndClose();
            })
            .catch((error) => alert(error));
        }
      }
    },
    [setWebViewMessageData, api, cognitoUser?.id]
  );

  useEffect(() => {
    // For Android
    document.addEventListener("message", handleMessage);
    // For IOS
    window.addEventListener("message", handleMessage);
    return () => {
      document.removeEventListener("message", handleMessage);
      window.removeEventListener("message", handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason: any) => {
          if (reason === "backdropClick") {
            return;
          }
          onClose();
        }}
        sx={{
          "& .MuiDialogContent-root": {
            padding: "0px"
          },
          "& .MuiDialog-paper": {
            borderRadius: "8px",
            backgroundColor: "#fff",
            maxWidth: "480px",
            width: "100%",
            "@media screen and (max-width:926px)": {
              maxWidth: "480px",
              marginLeft: "4px",
              marginRight: "4px"
            }
          }
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            padding: "4px 16px 0px 16px"
          }}
        >
          <div className="w-full">
            <div className="w-full flex justify-between">
              <div className="w-full flex justify-center items-center -mr-[42px] text-[14px] font-['Inter'] font-[600] uppercase">
                {translate("navigation.new_charge")}
              </div>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              backgroundColor: "#fff",
              "& .RaToolbar-desktopToolbar": {
                backgroundColor: "#fff",
                minHeight: "40px",
                padding: "0px 16px 16px 16px"
              },
              "& .RaToolbar-mobileToolbar": {
                position: "relative !important",
                backgroundColor: "#fff",
                minHeight: "40px",
                padding: "0px 16px 16px 16px !important"
                // zIndex: 90
              },
              "& .RaCreate-noActions": {
                marginTop: 0
              }
            }}
          >
            <SimpleForm
              onSubmit={submit}
              // validate={validateDates}
              resolver={yupResolver(createValidationSchema())}
              className="create-form"
              id="save-create-appointment"
              sx={{
                // backgroundColor: "#fff",
                padding: "0px 16px !important",
                "& .MuiFilledInput-root": {
                  borderRadius: "8px",
                  border: "1px solid rgba(48, 47, 46, 0.06)"
                },
                "& .MuiFilledInput-input": {
                  height: "24px",
                  fontSize: "16px",
                  borderRadius: "8px",
                  fontFamily: "Inter",
                  border: "none"
                },
                "& .MuiFilledInput-input:focus": {
                  boxShadow: "none",
                  borderRadius: "8px !important"
                },
                "& .MuiFilledInput-root:before": {
                  border: "none !important"
                },
                "& .MuiFilledInput-root:after": {
                  border: "1px solid #b8722a !important",
                  top: "0px",
                  borderRadius: "8px",
                  transition: "none"
                },
                "& .MuiFilledInput-root.Mui-error:after": {
                  border: "1px solid #EF5B1B !important"
                },
                "& .MuiFilledInput-root.Mui-disabled": {
                  backgroundColor: "rgba(48, 47, 46, 0.06)"
                },
                "& .MuiTextField-root": {
                  marginTop: "3px",
                  marginBottom: "3px"
                },
                "& .MuiInputLabel-root": {
                  fontSize: "16px",
                  color: "rgba(48, 47, 46, 0.6)"
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#b8722a"
                },
                "& .MuiFormHelperText-root": {
                  fontSize: "12px",
                  marginTop: "2px",
                  marginBottom: "2px",
                  lineHeight: "14px"
                }
              }}
              toolbar={false}
            >
              <BookFromArtistCalendarFormHelper
                resetUserInputs={resetUserInputs}
              />
              <div className="w-full">
                <h5 className="mb-2">
                  {translate("appointment.select_customer")}:
                </h5>
                <div className="w-full flex gap-2 justify-between mb-3">
                  <CustomButton
                    variant={
                      userSelectMode === "walk-in" ? "primary" : "secondary"
                    }
                    // disabled={userSelectMode === "new" ? true : false}
                    type="button"
                    text={translate("order.walk_in")}
                    onClick={() => {
                      if (
                        findUserData?.findFirstUser?.id &&
                        userSelectMode === "search"
                      ) {
                        setIsCloseIconShaking(true);
                        setTimeout(() => {
                          setIsCloseIconShaking(false);
                        }, 500);
                        return;
                      }
                      setUserSelectMode("walk-in");
                      setResetUserInputs((prev) => !prev);
                    }}
                    sx={{ width: "95%" }}
                  />
                  <CustomButton
                    variant={
                      userSelectMode === "customers" ? "primary" : "secondary"
                    }
                    // disabled={userSelectMode === "customers" ? true : false}
                    type="button"
                    text={translate("appointment.customers")}
                    onClick={() => {
                      if (
                        findUserData?.findFirstUser?.id &&
                        userSelectMode === "search"
                      ) {
                        setIsCloseIconShaking(true);
                        setTimeout(() => {
                          setIsCloseIconShaking(false);
                        }, 500);
                        return;
                      }
                      setUserSelectMode("customers");
                      setResetUserInputs((prev) => !prev);
                    }}
                    sx={{ width: "95%" }}
                  />
                  <CustomButton
                    variant={
                      userSelectMode === "search" ? "primary" : "secondary"
                    }
                    // disabled={userSelectMode === "search" ? true : false}
                    type="button"
                    text={translate("appointment.search_by_phone")}
                    onClick={() => {
                      setUserSelectMode("search");
                      if (!findUserData?.findFirstUser?.id) {
                        setResetUserInputs((prev) => !prev);
                      }
                    }}
                    sx={{ width: "110%" }}
                  />
                </div>
                {userSelectMode === "customers" && (
                  <ReferenceInput
                    source="user_id"
                    reference="User"
                    filter={{
                      OR: [
                        {
                          booked_appointments: {
                            some: {
                              location_id: {
                                in: locationsData?.map(
                                  (location) => location.id
                                )
                              }
                            }
                          }
                        },
                        {
                          joined_company_id: permissions.companyId
                        }
                      ]
                    }}
                    enableGetChoices={({ q }) => {
                      return q?.trim()?.length > 2;
                    }}
                    perPage={10}
                  >
                    <AutocompleteInput
                      optionText={(value) =>
                        `${value?.first_name} ${value?.last_name}`
                      }
                      shouldRenderSuggestions={(val: any) => {
                        return val?.trim()?.length > 2;
                      }}
                      label={translate("appointment.customers")}
                      fullWidth
                      noOptionsText={translate(
                        "appointment.search_to_find_customers"
                      )}
                      isRequired
                    />
                  </ReferenceInput>
                )}
                {userSelectMode === "search" && (
                  <CheckUserByPhoneNumber
                    checkFunction={checkUser}
                    userId={findUserData?.findFirstUser?.id}
                    isCustomerSearchTriggered={isCustomerSearchTriggered}
                    setIsCustomerSearchTriggered={setIsCustomerSearchTriggered}
                  />
                )}
                {findUserData?.findFirstUser?.id &&
                  userSelectMode === "search" && (
                    <div
                      aria-hidden
                      className="flex w-min items-center gap-[8px] rounded-[20px] mb-3 transition-colors duration-200 ease-in-out"
                      style={{
                        backgroundColor: "rgba(184, 114, 42, 0.1)"
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "rgba(238, 234, 228, 0.9)",
                          color: "rgba(48, 47, 46, 0.95)",
                          fontFamily: "Inter",
                          fontSize: "18px"
                        }}
                        alt={
                          findUserData?.findFirstUser?.first_name || "avatar"
                        }
                        src={
                          findUserData?.findFirstUser?.photo_url || "monogram"
                        }
                      />
                      <div
                        className="max-w-[150px] truncate whitespace-nowrap text-[16px] font-[400] font-['Inter']"
                        style={{
                          color: "#B8722A"
                        }}
                      >
                        {`${findUserData?.findFirstUser?.first_name} ${findUserData?.findFirstUser?.last_name}`}
                      </div>
                      <IconButton
                        style={{ width: "40px", height: "40px" }}
                        className={isCloseIconShaking ? "shakeAnimation" : ""}
                        onClick={() => {
                          checkUser("");
                          setIsCustomerSearchTriggered(false);
                        }}
                      >
                        <CloseIcon
                          style={{
                            color: "#EF5B1B",
                            width: "22px",
                            height: "22px"
                          }}
                        />
                      </IconButton>
                    </div>
                  )}
                <div
                  className={`w-full h-[2px] mb-4 bg-[rgba(0,0,0,0.07)] ${
                    userSelectMode === "walk-in" ? "mt-4" : "mt-0"
                  }`}
                />
                {/* <h5 className="mb-2">
                  {translate("appointment.general_details")}:
                </h5> */}
                {locationsData && locationsData.length > 0 && (
                  <ReferenceInput
                    source="location.id"
                    reference="Location"
                    filter={{ company_id: permissions.companyId }}
                  >
                    <AutocompleteInput
                      optionText={(value) => value.nickname || value.name}
                      disabled={disabled}
                      label={translate("availability.location")}
                      fullWidth
                      defaultValue={locationsData?.[0]?.id}
                      validate={[required()]}
                    />
                  </ReferenceInput>
                )}
                <TextField
                  variant="filled"
                  type="number"
                  label={translate("order.amount")}
                  InputProps={{
                    inputProps: {
                      min: 0,
                      nullable: true,
                      "data-cy": "amount-input"
                    }
                  }}
                  value={amount.toString()}
                  onFocus={(e) => {
                    if (e.target.value === "0") {
                      setAmount("");
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value === "") {
                      setAmount(0);
                    }
                  }}
                  onWheel={(e) => {
                    // Prevent scroll wheel from changing the number while focused
                    (e.target as HTMLElement).blur();
                  }}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    if (e.target.value === "") {
                      setAmount("");
                    } else {
                      setAmount(value);
                    }
                  }}
                  fullWidth
                  sx={{
                    marginBottom: "21px !important",
                    "& .MuiFilledInput-input": {
                      paddingTop: "22px",
                      paddingBottom: "5px",
                      fontWeight: 400
                    },
                    "& .MuiFormHelperText-root": {
                      display: "none"
                    }
                  }}
                  required
                />
                <TextInput
                  source="description"
                  label={translate("order.description")}
                  fullWidth
                />
                {data?.artists && data?.artists?.length > 0 && (
                  <AutocompleteInput
                    source="artist.id"
                    choices={
                      data?.artists?.map((item: any) => ({
                        id: item?.id,
                        name: `${item?.user?.first_name} ${item?.user?.last_name}`
                      })) || []
                    }
                    optionText={(value) => value?.name}
                    disabled={disabled}
                    label={translate("appointmentservices.artist")}
                    fullWidth
                    // validate={[required()]}
                  />
                )}
              </div>
            </SimpleForm>
            {isCustomErrors && (
              <Dialog
                open={isCustomErrors}
                onClose={() => setErrors({})}
                sx={{
                  "& .MuiDialogContent-root": {
                    padding: "0px"
                  },
                  "& .MuiDialog-paper": {
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    maxWidth: "520px",
                    width: "100%"
                  }
                }}
              >
                <DialogTitle
                  sx={{ display: "flex", padding: "4px 16px 0px 16px" }}
                >
                  <div className="w-full flex justify-center items-center ml-[-12px] text-[14px] font-['Inter'] font-[600]" />
                  <IconButton onClick={() => setErrors({})}>
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  <div className="w-full flex flex-col justify-center items-center px-2">
                    <FailedOrderIcon />

                    <p className="text-[24px] font-['OptimaLTStd'] font-[500] text-mono-primaryTextColor mt-4">
                      {errors?.networkError
                        ? "Something went wrong"
                        : translate(
                            "appointment.you_need_to_fill_required_fields"
                          )}
                    </p>
                    <div className="my-6">
                      {Object.values(errors).map(
                        (error: any, index: number) => (
                          <p
                            key={index}
                            className="text-[18px] font-['OptimaLTStd'] font-[500] text-mono-primaryTextColor mt-1"
                          >
                            {errors?.networkError ? "" : index + 1 + "."}{" "}
                            {error}
                          </p>
                        )
                      )}
                    </div>
                  </div>
                </DialogContent>
                <DialogActions>
                  <div className="w-full flex justify-center mb-4">
                    <CustomButton
                      variant="primary"
                      text={translate("appointment.got_it")}
                      onClick={() => setErrors({})}
                    />
                  </div>
                </DialogActions>
              </Dialog>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ paddingX: "16px" }}>
          <div className="w-full flex justify-between items-center">
            <div />
            <CustomButton
              type="submit"
              text={translate("appointment.charge")}
              form="save-create-appointment"
              submitting={isSaving}
              endIcon={<div />}
              disabled={isSaving || disabled || !amount}
            />
          </div>
        </DialogActions>
      </Dialog>
      {/* <Dialog
        open={!!webViewMessageData?.status}
        onClose={(_, reason: any) => {
          if (reason === "backdropClick") {
            return;
          }
          onClose();
        }}
        sx={{
          "& .MuiDialogContent-root": {
            padding: "0px"
          },
          "& .MuiDialog-paper": {
            borderRadius: "8px",
            backgroundColor: "#fff",
            maxWidth: "480px",
            width: "100%",
            "@media screen and (max-width:926px)": {
              maxWidth: "480px"
            }
          }
        }}
      >
        <DialogContent>
          <SimpleForm onSubmit={() => {}} toolbar={false}>
            <div className="w-full flex flex-col justify-center items-center pt-6">
              {error && <FailedOrderIcon />}
              {!error && webViewMessageData?.status !== "timeout" && (
                <FullyPaidOrderIcon />
              )}
              {!error && webViewMessageData?.status === "timeout" && (
                <TimeoutOrderIcon />
              )}
              {error && (
                <>
                  <p className="text-[24px] font-['OptimaLTStd'] font-[500] text-mono-primaryTextColor mt-4">
                    Payment Failed
                  </p>
                  <p className="text-[16px] font-['OptimaLTStd'] text-center font-[500] text-mono-primaryTextColor mt-4">
                    {error}
                  </p>
                </>
              )}
              {!error && (
                <p
                  data-cy="successful-payment"
                  className="text-[24px] font-['OptimaLTStd'] font-[500] text-mono-primaryTextColor mt-4"
                >
                  {typeof webViewMessageData?.message === "string"
                    ? webViewMessageData?.message
                    : "Successful Payment"}
                </p>
              )}
              {webViewMessageData?.amount &&
                webViewMessageData?.cardBrand &&
                webViewMessageData?.lastFourDigits && (
                  <div className="flex gap-[4px] text-[14px] text-mono-primaryTextColor font-[400] font-['Inter']">
                    <span>${webViewMessageData?.amount}</span>
                    <span>{`• ${webViewMessageData?.cardBrand}`}</span>
                    <span>{`*${webViewMessageData?.lastFourDigits}`}</span>
                  </div>
                )}
              <div className="mt-4 mb-8">
                <CustomButton
                  cyId="charge-got-it-button"
                  text="Go to order"
                  onClick={() => {
                    navigate(`/dashboard/Walk-in-order/${createdOrderId}/show`);
                  }}
                />
              </div>
            </div>
          </SimpleForm>
        </DialogContent>
      </Dialog> */}
    </>
  );
}
